import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../modal-new";
import QuestionDisplay from "../../../views/athlete-portal/profile/components/QuestionDisplay";
import submit from "submit-form";
import Select from "../../inputs/select";
import SvgIcon from "../../SvgIcon";

const QuestionnaireTemplates = ({
  athlete,
  userToLoadInformation,
  organizationOwner,
}) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const templates =
    organizationOwner?.questionnaireTemplates?.templates ||
    userToLoadInformation?.questionnaireTemplates?.templates ||
    [];
  const defaultTemplate =
    organizationOwner?.questionnaireTemplates?.defaultTemplate ||
    userToLoadInformation?.questionnaireTemplates?.defaultTemplate ||
    [];

  const { _id: athleteId } = athlete;

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [mobileOptions, setMobileOptions] = useState([]);

  useEffect(() => {
    if (templates && templates.length) {
      const athleteTemplateId = athlete.questionnaire?.templateId;

      const athleteTemplate = templates.find(
        (template) => template._id === athleteTemplateId
      );

      const defaultTemplateOb = templates.find(
        (template) => template._id === defaultTemplate
      );

      const mobileOptions = templates.map((template) => ({
        value: template._id,
        label: template.title,
      }));

      const selectedTemplate =
        athleteTemplate || defaultTemplateOb || templates[0];

      setMobileOptions(mobileOptions);
      setSelectedTemplate(selectedTemplate);
    }
  }, [templates]);

  const handleSetDefault = () => {
    const isDefault = selectedTemplate._id === defaultTemplate;
    const url = isDefault
      ? "/actions/questionnaire/remove-default-template"
      : "/actions/questionnaire/add-default-template";

    if (
      process.browser &&
      selectedTemplate._id === defaultTemplate &&
      !confirm(t("questionnaireTemplates.removeDefaultConfirm"))
    ) {
      return;
    }

    submit(url, {
      method: "POST",
      body: {
        templateId: selectedTemplate._id,
        userToRequest: organizationOwner?._id || userToLoadInformation._id,
        ...(organizationOwner && {
          organizationOwner: organizationOwner._id,
        }),
      },
    });
  };

  const handleAssignTemplate = () => {
    submit("/actions/questionnaire/assign-template", {
      method: "POST",
      body: {
        athleteId,
        templateId: selectedTemplate._id,
        userToRequest: userToLoadInformation._id,
        // if we are in an organization case, we need to send the organization owner id
        // so athletes will be taken from the coach that owns them, but
        // the templates will be taken from the organization owner
        ...(organizationOwner && { organizationOwner: organizationOwner._id }),
      },
    });
  };

  const removeTemplate = (id) => {
    if (confirm(t("questionnaireTemplates.deleteTemplateConfirm"))) {
      submit("/actions/questionnaire/remove-template", {
        method: "POST",
        body: {
          templateId: id,
          userToRequest: userToLoadInformation._id,
          ...(organizationOwner && {
            organizationOwner: organizationOwner._id,
          }),
        },
      });
    }
  };

  return (
    <Modal className={"checkin-template-modal"} title={t("questionnaireTemplates.chooseTemplate")}>
      <div className="body">
        {!templates.length ? (
          <section className="no-questions">
            <h4>{t("questionnaireTemplates.noTemplatesAvailable")}</h4>
            <p className="p5">
              {t("questionnaireTemplates.createFirstHint")}
            </p>
            <a
              role="button"
              data-button-type="primary"
              href="/actions/refresh?modal[type]=add-questionnaire-template"
            >
              {t("questionnaireTemplates.addNewTemplate")}
            </a>
          </section>
        ) : (
          <>
            <aside className="force-mobile-hidden">
              <p className="p5">{t("questionnaireTemplates.selectTemplate")}</p>
              <ul>
                {templates?.map((item, i) => {
                  return (
                    <li
                      key={`${item.title}-${i}-column`}
                      aria-current={
                        selectedTemplate?._id === item._id ? true : undefined
                      }
                      onClick={() => setSelectedTemplate(item)}
                    >
                      <p className="p1">{item.title}</p>
                      {selectedTemplate &&
                      selectedTemplate._id === item._id &&
                      selectedTemplate._id !== defaultTemplate ? (
                        <div
                          onClick={() => removeTemplate(item._id)}
                          className="questionnaire-delete-button"
                        >
                          <img
                            src="/img/new-icons/red-trash.svg"
                            alt={t("common:delete")}
                          />
                        </div>
                      ) : (
                        <></>
                      )}
                    </li>
                  );
                })}
              </ul>
            </aside>
            <section className="mobile-templates-list only-show-mobile">
              <p className="p5">{t("questionnaireTemplates.selectTemplate")}</p>
              <div className="select">
                <Select
                  instanceId="header-athletes"
                  autosize={false}
                  matchPos="start"
                  clearable={false}
                  options={mobileOptions}
                  simpleValue
                  onChange={(value) => {
                    const template = templates.find(
                      (templ) => templ._id === value
                    );

                    setSelectedTemplate(template);
                  }}
                  value={selectedTemplate?.title}
                />
              </div>
            </section>
            {selectedTemplate && (
              <section>
                <header>
                  <h5>{selectedTemplate && String(selectedTemplate.title)}</h5>
                  <div>
                    <button
                      onClick={() => removeTemplate(selectedTemplate._id)}
                      data-button-type="text-white"
                      data-icon-only
                      sz="medium"
                    >
                      <img src="/img/new-icons/red-trash.svg" alt={t("common:delete")} />
                    </button>
                    <a
                      className="force-mobile-hidden"
                      role="button"
                      data-button-type="tertiary"
                      href={`/actions/refresh?modal[type]=add-questionnaire-template&templateId=${selectedTemplate._id}`}
                    >
                      {t("questionnaireTemplates.editTemplate")}
                    </a>
                    <a
                      className="only-show-mobile"
                      href={`/actions/refresh?modal[type]=add-questionnaire-template&templateId=${selectedTemplate._id}`}
                      role="button"
                      data-button-type="tertiary"
                    >
                      <img src="/img/new-icons/edit-pencil.svg" alt={t("common:edit")} />
                    </a>
                  </div>
                </header>
                <section id="questionnaire-modal-templates-body-questions">
                  {selectedTemplate &&
                    selectedTemplate.questions.map((question) => (
                      <QuestionDisplay
                        body={question.body}
                        title={question.title}
                        type={question.type}
                        id={question._id}
                        key={question._id}
                        templateDisplay={true}
                      />
                    ))}
                </section>
              </section>
            )}
          </>
        )}
      </div>
      {selectedTemplate && (
        <>
          {/* footer for desktop */}
          <footer className="acc-template-footer force-mobile-hidden">
            <aside>
              <a
                href="/actions/refresh?modal[type]=add-questionnaire-template"
                role="button"
                data-button-type="outline"
              >
                {t("questionnaireTemplates.addNewTemplate")}
              </a>

              <div
                onClick={handleSetDefault}
                role="button"
                data-button-type="outline"
              >
                <SvgIcon
                  src={`/img/new-icons/${
                    selectedTemplate?._id === defaultTemplate ? "" : "un"
                  }checked-square.svg`}
                  noFilterOnLight={
                    selectedTemplate?._id === defaultTemplate ? true : false
                  }
                />
                <p className="p4">{t("questionnaireTemplates.setAsDefault")}</p>
              </div>
            </aside>
            <button
              onClick={handleAssignTemplate}
              role="button"
              data-button-type="primary"
            >
              {t("common:save")}
            </button>
          </footer>
          {/* footer for mobile */}
          <footer className="acc-template-footer force-only-show-mobile">
            <div className="div-wrapper-footer-acc">
              <div onClick={handleSetDefault}>
                <SvgIcon
                  src={`/img/new-icons/${
                    selectedTemplate?._id === defaultTemplate ? "" : "un"
                  }checked-square.svg`}
                />
                <p className="p4">{t("questionnaireTemplates.setAsDefault")}</p>
              </div>
              <div>
                <a
                  href="/actions/refresh?modal[type]=add-questionnaire-template"
                  role="button"
                  data-button-type="outline"
                >
                  {t("questionnaireTemplates.addNewTemplate")}
                </a>
                <button
                  onClick={handleAssignTemplate}
                  role="button"
                  data-button-type="primary"
                >
                  {t("common:save")}
                </button>
              </div>
            </div>
          </footer>
        </>
      )}
    </Modal>
  );
};

export default QuestionnaireTemplates;
